<template>
  <PurchaseHeader>
    <div class="bento-item logistics">
      <CustomLoader ref="loader"/>

      <!-- Le parcours d'une marchandise : commandée, reçue, en stock, affectée à un dossier -->
      <div class="flow">
        <div class="flow-step">
          <span class="flow-icon"><span class="fas fa-truck-ramp-box"></span></span>
          <div class="flow-text">
            <strong>{{ toReceive.length }}</strong>
            <span>achat{{ toReceive.length > 1 ? 's' : '' }} à recevoir</span>
            <small>{{ linesToReceive }} article{{ linesToReceive > 1 ? 's' : '' }} en attente</small>
          </div>
        </div>
        <span class="flow-arrow fas fa-arrow-right"></span>
        <div class="flow-step">
          <span class="flow-icon"><span class="fas fa-boxes-stacked"></span></span>
          <div class="flow-text">
            <strong>{{ formatAmount(stockValue) || '0,00 €' }}</strong>
            <span>en stock (HT)</span>
            <small>{{ stock.length }} article{{ stock.length > 1 ? 's' : '' }} non affecté{{ stock.length > 1 ? 's' : '' }}</small>
          </div>
        </div>
        <span class="flow-arrow fas fa-arrow-right"></span>
        <div class="flow-step end">
          <span class="flow-icon"><span class="fas fa-folder-open"></span></span>
          <div class="flow-text">
            <strong>Affecté à un dossier</strong>
            <small>le coût rejoint la marge du dossier</small>
          </div>
        </div>
        <div class="search">
          <span class="fas fa-magnifying-glass"></span>
          <input v-model="query" type="search" placeholder="Fournisseur, article, référence..." />
        </div>
      </div>

      <div class="columns">
        <div class="panel">
          <header class="panel-head">
            <h2><span class="fas fa-truck-ramp-box"></span>À recevoir <span class="count">{{ shownToReceive.length }}</span></h2>
            <p class="hint">Marchandises commandées. Réceptionnez-les à leur livraison, en totalité ou en partie : elles passent alors en stock.</p>
          </header>
          <p v-if="shownToReceive.length === 0" class="empty">{{ query ? 'Aucun résultat.' : 'Toutes les marchandises commandées ont été reçues.' }}</p>
          <article v-for="purchase in shownToReceive" :key="purchase.purchaseId" class="delivery">
            <div class="delivery-head">
              <div class="delivery-title">
                <strong>{{ purchase.supplierName || 'Fournisseur' }}</strong>
                <span class="muted" :title="purchase.label">{{ purchaseTitle(purchase) }} · {{ formatDate(purchase.invoiceDate) }}</span>
              </div>
              <StatusBadge :status="purchase.deliveryStatus" :status-enum="DELIVERY_STATUS_ENUM" />
            </div>
            <ul class="lines">
              <li v-for="line in purchase.lines" :key="line.lineId" :class="{done: line.remainingQuantity <= 0}">
                <div class="line-text">
                  <span class="designation" :title="line.designation">{{ line.designation }}</span>
                  <span class="muted">{{ formatQuantity(line.receivedQuantity, line.unit) }} / {{ formatQuantity(line.quantity, line.unit) }}</span>
                </div>
                <div class="bar"><span :style="{width: progressOf(line) + '%'}"></span></div>
              </li>
            </ul>
            <div class="card-actions">
              <button type="button" class="row-button" title="Ouvrir l'achat" @click="$refs.purchaseModal.openModal({id: purchase.purchaseId})"><span class="fas fa-up-right-from-square"></span>Ouvrir</button>
              <button type="button" class="row-button primary" @click="receive(purchase)"><span class="fas fa-truck-ramp-box"></span>Réceptionner</button>
            </div>
          </article>
        </div>

        <div class="panel">
          <header class="panel-head">
            <h2><span class="fas fa-boxes-stacked"></span>En stock <span class="count">{{ shownStock.length }}</span></h2>
            <p class="hint">Marchandises achetées qui ne sont affectées à aucun dossier. Affectez-les à un dossier au moment de les utiliser. <router-link to="/purchase-articles">Statistiques par article</router-link></p>
          </header>
          <div v-if="shownShortages.length > 0" class="shortages">
            <div class="shortages-head">
              <strong>À approvisionner <span class="count warn">{{ shownShortages.length }}</span></strong>
              <span class="muted">Affecté aux dossiers au-delà du stock : sans coût tant que l'achat n'est pas arrivé.</span>
            </div>
            <div v-for="article in shownShortages" :key="article.articleId" class="shortage">
              <div class="shortage-main">
                <strong class="designation" :title="article.name">{{ article.name }}</strong>
                <span class="muted">{{ shortageOrders(article) }}</span>
              </div>
              <div class="shortage-side">
                <strong class="negative">{{ formatQuantity(-article.pendingQuantity, article.unit) }}</strong>
                <button v-if="article.inStockQuantity + article.pendingQuantity > 0" type="button" class="row-button" title="Du stock est arrivé : le rattacher aux dossiers" @click="regularize(article)"><span class="fas fa-rotate"></span>Régulariser</button>
              </div>
            </div>
          </div>
          <p v-if="shownStock.length === 0" class="empty">{{ query ? 'Aucun résultat.' : 'Rien en stock : tout ce qui est acheté est affecté à un dossier.' }}</p>
          <article v-for="line in shownStock" :key="line.purchaseId + '|' + line.lineId" class="stock-line">
            <div class="stock-main">
              <strong class="designation" :title="line.designation">{{ line.designation }}</strong>
              <span v-if="articleName(line)" class="article-tag" title="Article du catalogue">{{ articleName(line) }}</span>
              <span class="muted" :title="purchaseTitle(line)">{{ line.supplierName || 'Fournisseur' }} · {{ purchaseTitle(line) }}</span>
              <span v-if="line.receivedQuantity < line.quantity" class="pending" title="Pas encore entièrement reçu"><span class="fas fa-truck"></span>Reçu {{ formatQuantity(line.receivedQuantity, line.unit) }} sur {{ formatQuantity(line.quantity, line.unit) }}</span>
            </div>
            <div class="stock-side">
              <div class="stock-quantity">
                <strong>{{ formatQuantity(line.availableQuantity, line.unit) }}</strong>
                <span class="muted">{{ formatAmount(line.availableQuantity * line.unitCostHt) || '0,00 €' }} HT</span>
              </div>
              <button v-if="orderValues" type="button" class="row-button primary" @click="$refs.stockAssignModal.openModal({line, orderValues})"><span class="fas fa-folder-open"></span>Affecter</button>
            </div>
          </article>
        </div>
      </div>
    </div>

    <ReceptionModal ref="receptionModal" :callback="loadData" />
    <StockAssignModal ref="stockAssignModal" :callback="loadData" />
    <PurchaseModal ref="purchaseModal" :callback="loadData" />
  </PurchaseHeader>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faArrowRight, faBoxesStacked, faFolderOpen, faMagnifyingGlass, faRotate, faTruck, faTruckRampBox, faUpRightFromSquare} from "@fortawesome/free-solid-svg-icons";
import CustomLoader from "@/components/CustomLoader.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import PurchaseHeader from "@/purchase/components/PurchaseHeader.vue";
import PurchaseModal from "@/purchase/components/modals/PurchaseModal.vue";
import ReceptionModal from "@/purchase/components/modals/ReceptionModal.vue";
import StockAssignModal from "@/purchase/components/modals/StockAssignModal.vue";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {LogisticsController} from "@/purchase/controllers/LogisticsController";
import {loadOrderValues} from "@/purchase/services/OrderChoiceService";
import {NotificationService} from "@vues/v-common";
import {DELIVERY_STATUS_ENUM, formatAmount, formatDate, formatQuantity} from "@/purchase/utils";
library.add(faArrowRight, faBoxesStacked, faFolderOpen, faMagnifyingGlass, faRotate, faTruck, faTruckRampBox, faUpRightFromSquare);

// Logistique des achats : ce qu'il reste à recevoir des fournisseurs et ce qui est en stock (acheté, pas encore affecté à un dossier).
// Rien n'est saisi ici qui ne soit déjà dans les achats : réceptionner met à jour la livraison de l'achat, affecter ajoute une part
// de quantité à sa ventilation.
export default {
  name: 'LogisticsView',
  components: {PurchaseHeader, CustomLoader, StatusBadge, PurchaseModal, ReceptionModal, StockAssignModal},
  data() {
    return {
      toReceive: [],
      stock: [],
      articles: [],             // catalogue : donne le nom de l'article d'une ligne de stock
      query: '',                // filtre commun aux deux colonnes
      orderValues: undefined,   // dossiers proposés à l'affectation, absents sans l'option production
      DELIVERY_STATUS_ENUM
    };
  },
  async mounted() {
    this.orderValues = await loadOrderValues();
    await this.loadData();
  },
  computed: {
    // Articles encore attendus, toutes livraisons confondues
    linesToReceive() {
      return this.toReceive.reduce((sum, purchase) => sum + purchase.lines.filter(line => line.remainingQuantity > 0).length, 0);
    },
    stockValue() {
      return this.stock.reduce((sum, line) => sum + line.availableQuantity * line.unitCostHt, 0);
    },
    shownToReceive() {
      const query = this.normalizedQuery;
      if (!query) return this.toReceive;
      return this.toReceive.filter(purchase => this.matches(query, [purchase.supplierName, purchase.reference, purchase.label, ...purchase.lines.map(line => line.designation)]));
    },
    shownStock() {
      const query = this.normalizedQuery;
      if (!query) return this.stock;
      return this.stock.filter(line => this.matches(query, [line.supplierName, line.reference, line.label, line.designation, this.articleName(line)]));
    },
    // Articles affectés à des dossiers au-delà du stock : leur solde est négatif
    shownShortages() {
      const query = this.normalizedQuery;
      return this.articles.filter(article => article.pendingQuantity > 0 && (!query || this.matches(query, [article.name, article.reference])));
    },
    orderLabels() {
      return new Map((this.orderValues ?? []).map(order => [String(order.value), order.label]));
    },
    normalizedQuery() {
      return this.normalize(this.query);
    },
  },
  methods: {
    formatAmount,
    formatDate,
    formatQuantity,
    normalize(text) {
      return String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    },
    matches(query, texts) {
      return texts.some(text => this.normalize(text).includes(query));
    },
    // Part de la quantité commandée déjà reçue (en %)
    progressOf(line) {
      return line.quantity > 0 ? Math.min(100, Math.round(line.receivedQuantity / line.quantity * 100)) : 0;
    },
    // Nom de l'article du catalogue auquel la ligne est rattachée, s'il y en a un
    articleName(line) {
      return line.articleId ? this.articles.find(article => article.articleId === String(line.articleId))?.name : undefined;
    },
    // Dossiers qui attendent l'article : « N°12 - Titre (3 m) »
    shortageOrders(article) {
      return article.byOrder
          .filter(order => order.pendingQuantity > 0)
          .map(order => `${this.orderLabels.get(String(order.orderId)) ?? 'Dossier'} (${formatQuantity(order.pendingQuantity, article.unit)})`)
          .join(' · ');
    },
    async regularize(article) {
      const res = await LogisticsController.regularize({articleId: article.articleId});
      if (res && !res.errors) {
        const done = res.regularizedQuantity > 0;
        (new NotificationService).addNotification(done ? "success" : "warning", done ? "Quantités régularisées sur les achats" : "Le stock ne couvre pas encore ces quantités");
        await this.loadData();
      }
    },
    purchaseTitle(purchase) {
      return [purchase.reference, purchase.label].filter((part, index, parts) => part && parts.indexOf(part) === index).join(' - ');
    },
    async loadData() {
      this.$refs.loader?.startLoading();
      const [toReceive, stock, articles] = await Promise.all([LogisticsController.getToReceive(), LogisticsController.getStock(), ArticleController.getStats()]);
      this.toReceive = toReceive && !toReceive.errors ? toReceive : [];
      this.stock = stock && !stock.errors ? stock : [];
      this.articles = articles && !articles.errors ? articles : [];
      this.$refs.loader?.stopLoading();
    },
    receive(purchase) {
      this.$refs.receptionModal.openModal({purchaseId: purchase.purchaseId, supplierName: purchase.supplierName});
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

h1
  color THEME_COLOR_PRIMARY

  span
    color THEME_COLOR_PRIMARY_FADDED

.logistics
  position relative
  display flex
  flex-direction column
  gap 18px

// Parcours d'une marchandise, de la commande à l'affectation
.flow
  display flex
  flex-wrap wrap
  align-items center
  gap 12px

  .flow-step
    display flex
    align-items center
    gap 12px
    padding 10px 16px
    border 1px solid #e3e7ef
    border-radius 12px
    background #f8f9fd

    &.end
      border-style dashed
      background transparent

  .flow-icon
    display flex
    align-items center
    justify-content center
    flex 0 0 auto
    width 38px
    height 38px
    border-radius 10px
    background #eef2fb
    color THEME_COLOR_PRIMARY

  .flow-text
    display flex
    flex-direction column
    line-height 1.25

    strong
      font-size 17px
      color #2b3550

    span
      font-size 13px
      color #4a5670

    small
      font-size 12px
      color #8a93a8

  .flow-arrow
    color #b4bccd
    font-size 13px

  .search
    display flex
    align-items center
    gap 8px
    margin-left auto
    padding 0 12px
    height 38px
    min-width 220px
    border 1px solid #e0e5f0
    border-radius 10px
    background white
    color #8a93a8

    input
      flex 1
      min-width 0
      border none
      outline none
      background transparent
      font-size 14px

.columns
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 18px
  align-items start

.panel
  display flex
  flex-direction column
  gap 10px
  padding 16px
  border 1px solid #e3e7ef
  border-radius 14px
  background white

.panel-head
  h2
    display flex
    align-items center
    gap 8px
    margin 0 0 4px
    font-size 16px
    color #2b3550

    > .fas
      color THEME_COLOR_PRIMARY
      font-size 14px

  .count
    padding 1px 8px
    border-radius 10px
    background #eef2fb
    color THEME_COLOR_PRIMARY
    font-size 12px

  .hint
    margin 0
    font-size 12.5px
    color #8a93a8

    a
      color THEME_COLOR_PRIMARY
      font-weight 600

.shortages
  display flex
  flex-direction column
  gap 8px
  padding 10px 12px
  border 1px solid #ecd9a6
  border-radius 12px
  background #fffbee

  .shortages-head
    display flex
    flex-direction column
    gap 2px

    strong
      color #7a5600
      font-size 14px

    .count.warn
      margin-left 6px
      padding 1px 8px
      border-radius 10px
      background #f6e3a8
      color #7a5600
      font-size 12px

  .shortage
    display flex
    align-items center
    justify-content space-between
    gap 12px

  .shortage-main
    display flex
    flex-direction column
    min-width 0

    .designation, .muted
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

  .shortage-side
    display flex
    align-items center
    gap 12px
    flex 0 0 auto

  .negative
    color #c0392b
    font-variant-numeric tabular-nums

.empty
  margin 0
  padding 22px 12px
  border 1px dashed #d6dcea
  border-radius 12px
  text-align center
  font-size 13.5px
  color #8a93a8

.muted
  color #6f7a93
  font-size 13px

// Livraison attendue
.delivery
  padding 12px 14px
  border 1px solid #e6eaf2
  border-left 4px solid #9db0dd
  border-radius 12px
  background #fbfcfe

  .delivery-head
    display flex
    align-items flex-start
    justify-content space-between
    gap 10px

  .delivery-title
    display flex
    flex-direction column
    min-width 0

    strong
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

    .muted
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

  .lines
    margin 10px 0 0
    padding 0
    list-style none
    display flex
    flex-direction column
    gap 8px

    li
      &.done
        opacity .5

    .line-text
      display flex
      justify-content space-between
      gap 12px
      font-size 13.5px

    .designation
      min-width 0
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

    .muted
      flex 0 0 auto
      font-variant-numeric tabular-nums

  .bar
    height 5px
    margin-top 3px
    border-radius 3px
    background #e8ecf5
    overflow hidden

    span
      display block
      height 100%
      border-radius 3px
      background #7f97d6

  .card-actions
    display flex
    justify-content flex-end
    gap 8px
    margin-top 10px

// Marchandise en stock
.stock-line
  display flex
  align-items center
  justify-content space-between
  gap 14px
  padding 12px 14px
  border 1px solid #e6eaf2
  border-left 4px solid #8cc7a1
  border-radius 12px
  background #fbfdfc

  .stock-main
    display flex
    flex-direction column
    min-width 0

    .designation
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

    .muted
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

  .article-tag
    align-self flex-start
    margin 2px 0
    padding 0 8px
    border-radius 10px
    background #eef2fb
    color THEME_COLOR_PRIMARY
    font-size 11.5px
    font-weight 600

  .pending
    margin-top 2px
    font-size 12px
    color #9a6b00

    .fas
      margin-right 5px
      font-size 11px

  .stock-side
    display flex
    align-items center
    gap 14px
    flex 0 0 auto

  .stock-quantity
    display flex
    flex-direction column
    align-items flex-end
    font-variant-numeric tabular-nums

    strong
      font-size 16px

.row-button
  display inline-flex
  align-items center
  gap 6px
  height 30px
  padding 0 12px
  border 1px solid #e0e5f0
  border-radius 8px
  background white
  color #4a5670
  font-size 12.5px
  font-weight 600
  cursor pointer
  transition all .15s ease

  &:hover
    background #eef2fb
    border-color #c5d0ea
    color THEME_COLOR_PRIMARY

  &.primary
    border-color THEME_COLOR_PRIMARY
    background THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE

    &:hover
      background CTRL_PRIMARY_HOVER

  span
    font-size 11px

@media (max-width 1100px)
  .columns
    grid-template-columns minmax(0, 1fr)

  .flow .search
    margin-left 0
    width 100%

@media (max-width 700px)
  .flow
    .flow-arrow
      display none

    .flow-step
      width 100%

  .stock-line
    flex-direction column
    align-items stretch

    .stock-side
      justify-content space-between
</style>
